/**
 * 文書（索引 Issue #498 → #642 / #722）。**editor が持つのはこれだけ**。
 *
 *   { format, version, fps, anchors{}, meta{} }
 *
 * 中身は**人が触ったものだけ**: 動かしたイベントの時刻（`anchors[key] = { ms }`）と、実装固有の入れ物 `meta`
 * （タイミング調節のテーマ `meta.theme`・設定の変化 `meta.settings`。コアは中身を見ない）。
 * ソースが生成した帯は開くたびに生成器が置き直すので文書には入らない。保存する形（プロジェクトファイル v4）への
 * 写しは `editorPorts/ocgDocument.js`。
 *
 * 編集ヘルパーは**新しい文書を返し、引数を書き換えない**（丸ごと置換で保存し、取り消しは組を戻すだけにするため）。
 *
 * 汎用の編集ソフトだったころ（Issue #642 より前）の文書はトラック・自由なオブジェクト・コンポジションも持っていた。
 * 読むときはそれらを捨てる（形式の名前と版は同じなので、古い文書も `anchors` と `meta` はそのまま読める）。
 */
import { readFps, DEFAULT_FPS } from './time';

export const DOCUMENT_FORMAT = 'synchro-editor-document';
export const DOCUMENT_VERSION = 1;

const isObj = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

export function emptyDocument(fps = DEFAULT_FPS) {
  return { format: DOCUMENT_FORMAT, version: DOCUMENT_VERSION, fps: readFps(fps), anchors: {}, meta: {} };
}

export const isDocument = (doc) => isObj(doc) && doc.format === DOCUMENT_FORMAT && doc.version === DOCUMENT_VERSION;

/** アンカーの上書き 1 件（人が動かした時刻・隠した印）。どちらも無ければ null */
export function normalizeAnchorOverride(v) {
  if (!isObj(v)) return null;
  const out = {};
  if (Number.isFinite(v.ms)) out.ms = Math.round(v.ms);
  if (v.hidden === true) out.hidden = true;
  return Object.keys(out).length ? out : null;
}

/** 読んだ文書を欠けの無い形に。この形式でなければ null（呼ぶ側が移行するか空を使う） */
export function normalizeDocument(doc) {
  if (!isDocument(doc)) return null;
  const base = emptyDocument(doc.fps);
  if (isObj(doc.anchors)) {
    for (const [key, v] of Object.entries(doc.anchors)) {
      const o = normalizeAnchorOverride(v);
      if (o) base.anchors[key] = o;
    }
  }
  if (isObj(doc.meta)) base.meta = { ...doc.meta };
  return base;
}

/** 正規化した文書（読めなければ空） */
export const readDocument = (doc, fps = DEFAULT_FPS) => normalizeDocument(doc) ?? emptyDocument(fps);

/** 実装固有の入れ物（テーマ・設定の変化など。コアは中身を見ない）。`null` はキーごと消す */
export const setMeta = (doc, key, value) => {
  const d = readDocument(doc);
  const meta = { ...d.meta };
  if (value == null) delete meta[key]; else meta[key] = value;
  return { ...d, meta };
};
